import React, { useEffect, useMemo, useState } from 'react';
import { getFirestore, collection, getDocs, query } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import './Membres.css';

interface Membre {
  id: string;
  photo?: string;
  loge?: string;
  loges?: string[];
  nom?: string;
  prenom?: string;
  adresse?: string;
  codePostal?: string;
  ville?: string;
  mail?: string;
  telephone?: string;
  statut?: string;
  grades?: string;
  fonction?: string;
  administrateur?: string;
}

function Membres() {
  const navigate = useNavigate();

  const [membres, setMembres] = useState<Membre[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [searchText, setSearchText] = useState('');
  const [sortColumn, setSortColumn] = useState<string>('');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  useEffect(() => {
    const fetchMembres = async () => {
      try {
        setLoading(true);
        const db = getFirestore();

        // Tous les membres
        const q = query(collection(db, 'Membres'));

        const snap = await getDocs(q);
        const data = snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) })) as Membre[];
        setMembres(data);
      } catch (e) {
        console.error(e);
        setError('Erreur lors du chargement des membres.');
      } finally {
        setLoading(false);
      }
    };

    fetchMembres();
  }, []);

  const handleSort = (col: string) => {
    if (sortColumn === col) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(col);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (col: string) => {
    if (sortColumn !== col) return '↕️';
    return sortDirection === 'asc' ? '↑' : '↓';
  };

  const filtered = useMemo(() => {
    const txt = searchText.trim().toLowerCase();
    if (!txt) return membres;

    return membres.filter((m) => {
      const hay = [
        m.nom,
        m.prenom,
        m.mail,
        m.grades,
        m.fonction,
        m.adresse,
        m.ville,
        m.codePostal,
        m.telephone,
        m.statut,
        Array.isArray(m.loges) ? m.loges.join(' ') : m.loge,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return hay.includes(txt);
    });
  }, [membres, searchText]);

  const sorted = useMemo(() => {
    const arr = [...filtered];
    if (!sortColumn) return arr;

    arr.sort((a: any, b: any) => {
      const av = (a[sortColumn] ?? '').toString();
      const bv = (b[sortColumn] ?? '').toString();
      const cmp = av.localeCompare(bv);
      return sortDirection === 'asc' ? cmp : -cmp;
    });

    return arr;
  }, [filtered, sortColumn, sortDirection]);

  const exportToExcel = () => {
    const dataToExport = sorted.map((m) => ({
      Photo: m.photo && m.photo !== 'Aucune photo' ? 'Oui' : 'Non',
      'Loge(s)': Array.isArray(m.loges) ? m.loges.join(', ') : m.loge || '',
      Nom: m.nom || '',
      Prénom: m.prenom || '',
      Adresse: m.adresse || '',
      'Code Postal': m.codePostal || '',
      Ville: m.ville || '',
      Mail: m.mail || '',
      Téléphone: m.telephone || '',
      Statut: m.statut || '',
      Grade: m.grades || '',
      Fonction: m.fonction || '',
      Administrateur: m.administrateur || 'non',
    }));

    const ws = XLSX.utils.json_to_sheet(dataToExport);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Membres');
    XLSX.writeFile(wb, 'membres.xlsx');
  };

  const handleModifier = (membreId: string) => {
    navigate(`/mapage/modifier-membre/${membreId}`);
  };

  if (loading) return <div>Chargement...</div>;
  if (error) return <div className="error-message">{error}</div>;

  return (
    <div className="membres-container">
      <h1>Liste des membres</h1>

      <div className="button-container">
        <div className="buttons-group">
          <button className="extraire-btn" onClick={exportToExcel}>
            Extraire
          </button>

          <button className="ajouter-btn" onClick={() => navigate('/mapage/ajouter-membre')}>
            Ajouter Membres
          </button>
        </div>

        <div className="search-container">
          <input
            type="text"
            className="search-input"
            placeholder="Rechercher par nom, prénom, mail, grade, fonction..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />
        </div>
      </div>

      {/* ✅ Fenêtre scrollable (horizontal + vertical) */}
      <div className="table-wrapper">
        <table className="membres-table">
          <thead>
            <tr>
              <th>Photo</th>

              <th onClick={() => handleSort('loges')} className="sortable-column">
                Loge(s) {getSortIcon('loges')}
              </th>

              <th onClick={() => handleSort('nom')} className="sortable-column">
                Nom {getSortIcon('nom')}
              </th>

              <th>Prénom</th>
              <th>Adresse</th>
              <th>Code Postal</th>
              <th>Ville</th>
              <th>Mail</th>
              <th>Téléphone</th>

              <th onClick={() => handleSort('statut')} className="sortable-column">
                Statut {getSortIcon('statut')}
              </th>

              <th onClick={() => handleSort('grades')} className="sortable-column">
                Grade {getSortIcon('grades')}
              </th>

              <th onClick={() => handleSort('fonction')} className="sortable-column">
                Fonction {getSortIcon('fonction')}
              </th>

              <th onClick={() => handleSort('administrateur')} className="sortable-column">
                Administrateur {getSortIcon('administrateur')}
              </th>

              <th>Actions</th>
            </tr>
          </thead>

          <tbody>
            {sorted.length === 0 ? (
              <tr>
                <td colSpan={14} style={{ textAlign: 'center', padding: 20 }}>
                  Aucun membre trouvé.
                </td>
              </tr>
            ) : (
              sorted.map((m) => (
                <tr key={m.id}>
                  <td>
                    {m.photo && m.photo !== 'Aucune photo' ? (
                      <img src={m.photo} alt={`${m.nom} ${m.prenom}`} className="membre-photo" />
                    ) : (
                      'Aucune photo'
                    )}
                  </td>

                  <td>{Array.isArray(m.loges) ? m.loges.join(', ') : m.loge || ''}</td>
                  <td>{m.nom}</td>
                  <td>{m.prenom}</td>
                  <td>{m.adresse}</td>
                  <td>{m.codePostal}</td>
                  <td>{m.ville}</td>
                  <td>{m.mail}</td>
                  <td>{m.telephone}</td>
                  <td>{m.statut}</td>
                  <td>{m.grades}</td>
                  <td>{m.fonction}</td>
                  <td>{m.administrateur || 'non'}</td>

                  <td className="actions-cell">
                    <button
                      className="modifier-membre-btn"
                      onClick={() => handleModifier(m.id)}
                      title="Modifier ce membre"
                    >
                      Modifier
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default Membres;
